export class FlowProcessor {
  constructor(flowOrchestrator, sessionCoordinator, stepFlowManager) {
    this.flowOrchestrator = flowOrchestrator;
    this.sessionCoordinator = sessionCoordinator;
    this.stepFlowManager = stepFlowManager;
  }

  async processPostInputFlow(
    currentStep,
    sessionData,
    input,
    stepOutput,
    engine,
    callbacks = {}
  ) {
    const updatedSessionInfo = this.sessionCoordinator.getSessionInfo();

    console.log("🔍 FlowProcessor - post input flow:", {
      hasMoreSteps: updatedSessionInfo.hasMoreSteps,
      currentStepIndex: updatedSessionInfo.currentStepIndex,
      totalSteps: updatedSessionInfo.totalSteps,
      currentStep: updatedSessionInfo.currentStep,
      hasPrompt: updatedSessionInfo.currentStep?.prompt ? true : false,
    });

    // Check if we've moved to the last step
    if (!updatedSessionInfo.hasMoreSteps) {
      console.log("🏁 No more steps - checking for final auto-advance step...");
      // If the current step (after move) is an auto-advance step, process it first
      const nextStep = updatedSessionInfo.currentStep;
      if (nextStep && !nextStep.prompt) {
        console.log(
          "🔄 Processing final auto-advance step before completion..."
        );
        // Process the auto-advance step and accumulate its output
        const autoAdvanceResult =
          await this.stepFlowManager.handleAutoAdvanceStep(
            nextStep,
            sessionData,
            stepOutput
          );

        // If the auto-advance step returns completion output, end the session and return
        if (autoAdvanceResult && autoAdvanceResult.output) {
          console.log(
            "✅ Final auto-advance step completed with output, ending session..."
          );
          // End the session since command is complete
          engine.endSession();
          return autoAdvanceResult;
        }
      }

      // Now handle completion
      return await this._handleCompletion(
        currentStep,
        sessionData,
        input,
        stepOutput,
        engine,
        callbacks
      );
    } else {
      return await this._handleMidStep(
        currentStep,
        sessionData,
        input,
        stepOutput,
        engine,
        callbacks
      );
    }
  }

  async _handleCompletion(
    currentStep,
    sessionData,
    input,
    stepOutput,
    engine,
    callbacks = {}
  ) {
    const result = await this.flowOrchestrator.handleCommandCompletion(
      currentStep,
      sessionData,
      input,
      stepOutput,
      callbacks
    );
    return await this._processFlowResult(result, true, engine);
  }

  async _handleMidStep(
    currentStep,
    sessionData,
    input,
    stepOutput,
    engine,
    callbacks
  ) {
    const result = await this.flowOrchestrator.handleMidStepFlow(
      currentStep,
      sessionData,
      input,
      stepOutput,
      callbacks
    );

    if (result.type === "nextStep") {
      const nextStepResult = await this.stepFlowManager.handleNextStep(
        result.stepOutput
      );

      // If handleNextStep processed the final auto-advance step and returned completion output,
      // we need to end the session
      if (nextStepResult && nextStepResult.output && !engine.isInSession()) {
        console.log(
          "✅ Final step completed via handleNextStep, session already ended"
        );
        return nextStepResult;
      }

      // Check if the engine is still in session after processing the next step
      if (nextStepResult && nextStepResult.output && !nextStepResult.prompt) {
        // This is completion output (has output but no prompt), check if session should end
        const sessionInfo = this.sessionCoordinator.getSessionInfo();
        console.log("🔍 FlowProcessor - checking if session should end:", {
          hasOutput: !!nextStepResult.output,
          hasPrompt: !!nextStepResult.prompt,
          hasMoreSteps: sessionInfo.hasMoreSteps,
          currentStepIndex: sessionInfo.currentStepIndex,
          totalSteps: sessionInfo.totalSteps,
          isInSession: engine.isInSession(),
        });
        if (!sessionInfo.hasMoreSteps) {
          console.log(
            "🏁 Ending session after final auto-advance step completion"
          );
          engine.endSession();
        } else {
          console.log("⚠️ Has more steps, NOT ending session");
        }
      }

      return nextStepResult;
    }

    return await this._processFlowResult(result, true, engine);
  }

  async _processFlowResult(flowResult, shouldEndSession, engine) {
    if (shouldEndSession) {
      // Only end session for completion types, NOT for redirects or startCommand
      // Redirects should preserve session state for seamless flow
      if (flowResult.type === "completion") {
        // debug: FlowProcessor ending session for completion
        engine.endSession();
      } else if (
        flowResult.type === "startCommand" ||
        flowResult.type === "redirect"
      ) {
        // debug:
        // debug: FlowProcessor preserving session for redirect/startCommand
        // Don't end session - let the redirect handle session state
      }
    }

    if (flowResult.type === "startCommand") {
      // Track parent chain for "back" navigation across redirects. Mirrors
      // the push done in handleRedirectCommand — this code path bypasses
      // that handler when a step's redirect produces a clean startCommand
      // result (no step output).
      const parentCommandName = engine.currentCommandName;
      if (parentCommandName) {
        if (!Array.isArray(engine.redirectParentChain)) {
          engine.redirectParentChain = [];
        }
        engine.redirectParentChain.push(parentCommandName);
      }
      return await engine.startCommand(flowResult.target, flowResult.data, {
        isRedirect: true,
      });
    }

    if (flowResult.type === "redirect") {
      // debug:
      // debug: FlowProcessor handling redirect with output:
      // Return the redirect result as-is for the redirect handlers to process
      return flowResult.result;
    }

    return flowResult.result || flowResult;
  }
}
